<template>
    <div>

        <PageHeader>
            <template v-if="offer">{{ offer.name || offer.offerId || offer.id }}</template>
            <template #side>
                <ButtonIcon :to="parentRoute">
                    {{ $t('goBack') }}
                </ButtonIcon>
            </template>
        </PageHeader>

        <Card :header="$t('offerDetails')" overlay :loading="offerLoading">

            <ErrorMessage :value="offerError" />

            <div v-if="offer" class="grid-template">

                <div class="label">{{ $t('id') }}:</div>
                <div class="value">{{ offer.id }}</div>

                <div class="label">{{ $t('offerId') }}:</div>
                <div class="value">{{ offer.offerId }}</div>

                <div class="label">{{ $t('ean') }}:</div>
                <div class="value">{{ offer.ean || '-' }}</div>

                <div class="label">{{ $t('name') }}:</div>
                <div class="value">{{ offer.name || '-' }}</div>

                <div class="label">{{ $t('info') }}:</div>
                <div class="value">{{ offer.info || '-' }}</div>

                <div class="label">{{ $t('code') }}:</div>
                <div class="value">{{ offer.code || '-' }}</div>

                <div class="label">{{ $t('price') }}:</div>
                <div class="value">{{ offer.price != null ? $format.currency(offer.price) : '-' }}</div>

                <div class="label">{{ $t('offerType') }}:</div>
                <div class="value">
                    {{ offer.type ? $tx(`typesOfOffers.${offer.type}`, offer.type) : '-' }}
                    <template v-if="offer.format"> / {{ offer.format }}</template>
                </div>

                <div class="label">{{ $t('feed') }}:</div>
                <div class="value">{{ offer.feed || '-' }}</div>

                <div class="label">{{ $t('campaign') }}:</div>
                <div class="value">{{ offer.campaign?.name || '-' }}</div>

                <div class="label">{{ $t('updatedAt') }}:</div>
                <div class="value">{{ offer.updatedAt ? $format.datetime(offer.updatedAt) : '-' }}</div>

                <div class="label">{{ $t('lastAvailabilityChangedAt') }}:</div>
                <div class="value">{{ offer.lastAvailabilityChangedAt ? $format.datetime(offer.lastAvailabilityChangedAt) : '-' }}</div>

                <div class="label">{{ $t('lastInFeedAt') }}:</div>
                <div class="value">{{ offer.lastInFeedAt ? $format.datetime(offer.lastInFeedAt) : '-' }}</div>

                <div class="label">{{ $t('url') }}:</div>
                <div class="value">
                    <a v-if="offer.url" :href="offer.url" target="_blank" rel="noopener noreferrer">{{ offer.url }}</a>
                    <span v-else>-</span>
                </div>

                <div class="label">{{ $t('image') }}:</div>
                <div class="value">
                    <div v-if="offer.image" class="mb-1">{{ offer.image }}</div>
                    <ImageLoader
                        v-if="offer.image"
                        :src="offer.image"
                        alt=""
                        size="50"
                        image-height="auto"
                        class="mr-2 flex-shrink-0"
                    />
                    <span v-else>-</span>
                </div>

            </div>

        </Card>

        <Card :header="$t('products')" overlay :loading="productsCardLoading">
            <template #nav v-if="productsWithData.length">
                <div class="d-flex gap-2">
                    <ButtonIcon
                        variant="warning"
                        :disabled="requestOfferModeration.getAnyLoading() || requestCatalogOffers.getAnyLoading()"
                        :loading="requestOfferModeration.getLoading('delete')"
                        hide-label-while-loading
                        @click="ignoreProposal()"
                    >
                        {{ $t('ignoreProposal') }}
                    </ButtonIcon>
                </div>
            </template>

            <div v-if="productsWithData.length">
                <div v-for="productData in productsWithData" :key="productData.productId" class="mb-5">
                    <b-table-simple small borderless class="mb-0">
                        <b-thead class="thead-dark">
                            <b-tr class="border-bottom">
                                <b-th>{{ $t('id') }}</b-th>
                                <b-th>{{ $t('name') }}</b-th>
                                <b-th>{{ $t('author') }}</b-th>
                                <b-th>{{ $t('brand') }}</b-th>
                                <b-th>{{ $t('description') }}</b-th>
                                <b-th>{{ $t('source') }}</b-th>
                                <b-th>{{ $t('riskScore') }}</b-th>
                                <b-th>{{ $t('offerRiskScores') }}</b-th>
                                <b-th>{{ $t('productRiskScores') }}</b-th>
                            </b-tr>
                        </b-thead>
                        <b-tbody>
                            <b-tr>
                                <b-td>{{ productData.product?.id || '-' }}</b-td>
                                <b-td>{{ productData.product?.name || '-' }}</b-td>
                                <b-td>{{ productData.product?.author || '-' }}</b-td>
                                <b-td>{{ productData.product?.brand?.name || '-' }}</b-td>
                                <b-td>{{ productData.product?.description || '-' }}</b-td>
                                <b-td>{{ productData.product?.source || '-' }}</b-td>
                                <b-td>{{ productData.riskScore ?? '-' }}</b-td>
                                <b-td>
                                    <div v-html="formatRiskSection($t('offerRiskScores'), productData.offerRisks, riskLabelsOffer)"></div>
                                </b-td>
                                <b-td>
                                    <div v-html="formatRiskSection($t('productRiskScores'), productData.productRisks, riskLabelsProduct)"></div>
                                </b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>

                    <h6>{{ $t('variants') }}</h6>
                    <b-table-lite
                        :items="productData.variantsData"
                        :fields="variantsFields"
                        small
                        responsive
                        thead-tr-class="border-bottom"
                        head-variant="secondary"
                        details-td-class="border-bottom border-light"
                    >
                        <template #cell(manufacturer)="{ item }">
                            {{ item.manufacturer?.name || '-' }}
                        </template>
                        <template #cell(action)="{ item }">
                            <div class="text-right">
                                <ButtonIcon
                                    variant="success"
                                    size="sm"
                                    :disabled="requestCatalogOffers.getAnyLoading() || requestOfferModeration.getAnyLoading()"
                                    :loading="requestCatalogOffers.getLoading(`add-${item.gtin}`)"
                                    hide-label-while-loading
                                    @click="addOfferToVariant(item.gtin)"
                                >
                                    {{ $t('addOfferToThisVariant') }}
                                </ButtonIcon>
                            </div>
                        </template>
                        <template #row-details="{ item }">
                            <div class="d-flex gap-2 align-items-center accordion mb-2">
                                <ButtonIcon
                                    v-b-toggle="`accordion-offers-${item.gtin}`"
                                    variant="secondary"
                                    icon="chevron-down"
                                    flip-icon
                                    size="sm"
                                    class="expandable p-1"
                                    :disabled="requestVariantOffers.getLoading(item.gtin)"
                                />
                                <span>{{ $t('offers') }}:</span>
                                <b-spinner v-if="requestVariantOffers.getLoading(item.gtin)" small />
                                <Tooltip
                                    v-else-if="requestVariantOffers.getError(item.gtin)"
                                    type="alert-circle"
                                    :size="20"
                                    variant="danger"
                                    icon-variant="danger"
                                    :html="requestVariantOffers.getError(item.gtin)?.message"
                                />
                            </div>
                            <b-collapse :id="`accordion-offers-${item.gtin}`" visible>
                                <DataTable
                                    v-if="!requestVariantOffers.getLoading(item.gtin)"
                                    :items="requestVariantOffers.getData(item.gtin)?.items || []"
                                    :fields="offersFields"
                                    small
                                    borderless
                                    responsive
                                    thead-tr-class="border-bottom"
                                    :pagination="false"
                                    no-padding
                                >
                                    <template #empty>
                                        <div class="f-flex text-center">{{ $t('noOffers') }}</div>
                                    </template>
                                    <template #cell(product)="data">
                                        <div class="d-flex flex-column gap-2">
                                            <b-link v-if="data.item.product" :to="{ name: 'Offers', query: { product: data.item.product } }">
                                                {{ data.item.product }}
                                            </b-link>
                                            <div class="info text-secondary font-size-12">
                                                {{ data.item.offerId }}
                                            </div>
                                        </div>
                                    </template>

                                    <template #cell(ean)="data">
                                        <b-link :to="{ name: 'Offers', query: { ean: data.item.ean } }">
                                            {{ data.item.ean }}
                                        </b-link>
                                    </template>

                                    <template #cell(name)="data">
                                        <div class="d-flex">
                                            <ImageLoader :src="data.item.image" alt="" size="50" image-height="auto" class="mr-2 flex-shrink-0" />
                                            <div class="d-flex flex-column gap-2">
                                                <b-link class="title" :to="createItemUrl(data.item)">
                                                    {{ data.item.name }}
                                                </b-link>
                                                <div class="info text-secondary font-size-12">
                                                    {{ data.item.info }}
                                                </div>
                                            </div>
                                        </div>
                                    </template>

                                    <template #cell(feed)="data">
                                        <div class="d-flex flex-column gap-2">
                                            {{ data.item.feed }}
                                            <div v-if="data.item.campaign" class="info">
                                                {{ data.item.campaign.name }}
                                            </div>
                                        </div>
                                    </template>

                                    <template #cell(price)="data">
                                        <div class="nowrap">
                                            {{ $format.currency(data.item.price) }}
                                        </div>
                                    </template>

                                    <template #cell(type)="data">
                                        {{ translateOfferType(data.item.type) }}<br>
                                        <template v-if="data.item.format">{{ data.item.format }}</template>
                                    </template>

                                    <template #cell(hidden)="data">
                                        <BadgeHidden :value="data.item.hidden" />
                                    </template>

                                    <template #cell(active)="data">
                                        <div class="d-flex flex-column gap-2">
                                            <div><BadgeBoolean :value="data.item.active" :true-value-text="$t('available2')" :false-value-text="$t('unavailable2')" /></div>
                                            <div class="info text-secondary font-size-12">
                                                {{ $format.dateAndHour(data.item.lastAvailabilityChangedAt, 'yyyy-MM-dd') }}
                                            </div>
                                        </div>
                                    </template>
                                </DataTable>
                            </b-collapse>
                        </template>
                    </b-table-lite>
                </div>
            </div>
            <div v-else-if="!productsCardLoading">
                {{ $t('noProducts') }}
            </div>
        </Card>

    </div>
</template>



<script>
    import DataTable from '@/components/common/DataTable.vue';
    import ImageLoader from '@/components/common/ImageLoader.vue';
    import BadgeHidden from '@/components/common/BadgeHidden.vue';
    import BadgeBoolean from '@/components/common/BadgeBoolean.vue';
    import Tooltip from '@/components/common/Tooltip.vue';
    import { translateOfferType } from '@/services/offersService';

    export default {
        name: 'OffersModerationDetails',
        components: {
            DataTable,
            ImageLoader,
            BadgeHidden,
            BadgeBoolean,
            Tooltip,
        },
        data() {
            return {
                offerId: this.$route.params.id,
                requestOfferModeration: this.$req('offers/offerModeration'),
                requestCatalogOffers: this.$req('catalog/offers'),
                requestCatalogProducts: this.$req('catalog/products'),
                requestProductVariants: this.$req('catalog/variants'),
                requestVariantOffers: this.$req('catalog/variantOffers'),
            };
        },
        computed: {
            parentRoute() {
                return this.$route.params.parentUrl ? this.$router.resolve(this.$route.params.parentUrl).resolved : this.$route.matched.slice(-2)[0];
            },
            offer() {
                return this.requestOfferModeration.getData();
            },
            offerLoading() {
                return this.requestOfferModeration.getLoading();
            },
            offerError() {
                return this.requestOfferModeration.getError();
            },
            productsCardLoading() {
                return this.requestOfferModeration.getLoading('delete') || this.requestOfferModeration.getLoading() || this.requestCatalogOffers.getAnyLoading();
            },
            products() {
                return this.offer?.products || [];
            },
            productsWithData() {
                return this.products.map(productEntry => {
                    const productId = productEntry.productId;
                    const productDetails = this.requestCatalogProducts.getData(`get-${productId}`);
                    const variantsResponse = this.requestProductVariants.getData(`product-${productId}`);
                    return {
                        ...productEntry,
                        product: productDetails,
                        variantsData: (variantsResponse?.items || []).map(variantEntry => ({ ...variantEntry, _showDetails: true })),
                    };
                });
            },
            riskLabelsOffer() {
                return {
                    info_similarity: this.$t('riskInfoSimilarity'),
                    name_similarity: this.$t('riskNameSimilarity'),
                    offer_quantity: this.$t('riskOfferQuantity'),
                };
            },
            riskLabelsProduct() {
                return {
                    existing_variant: this.$t('riskExistingVariant'),
                    info_similarity: this.$t('riskInfoSimilarity'),
                    name_similarity: this.$t('riskNameSimilarity'),
                };
            },
            variantsFields() {
                return [
                    { key: 'gtin', label: this.$t('gtin') },
                    { key: 'code', label: this.$t('code') },
                    { key: 'manufacturer', label: this.$t('manufacturer') },
                    { key: 'action', label: '' },
                ];
            },
            offersFields() {
                return [
                    { key: 'product', label: this.$t('aggregateOfferId') },
                    { key: 'ean', label: this.$t('ean') },
                    { key: 'name', label: this.$t('name') },
                    { key: 'info', label: this.$t('info') },
                    { key: 'code', label: this.$t('code') },
                    { key: 'feed', label: this.$t('feedShop') },
                    { key: 'type', label: this.$t('offerType') },
                    { key: 'price', label: this.$t('price') },
                    { key: 'hidden', label: this.$t('hidden') },
                    { key: 'active', label: this.$t('availability') },
                ];
            },
        },
        watch: {
            products: {
                handler() {
                    this.fetchProductsData();
                },
                immediate: true,
            },
            productsWithData() {
                const variantGtins = this.productsWithData.flatMap(productEntry => productEntry.variantsData.map(variantEntry => variantEntry.gtin));
                variantGtins.forEach(variantGtin => {
                    if (!this.requestVariantOffers.getData(variantGtin)) {
                        this.fetchVariantOffers(variantGtin);
                    }
                });
            },
        },
        methods: {
            fetchOffer() {
                return this.requestOfferModeration.get({ urlParams: { offerId: this.offerId } }).catch(() => {});
            },
            formatRiskSection(title, data, labels) {
                if (!data || typeof data !== 'object') return '';
                const entries = Object.entries(data).filter(([, value]) => value !== null && value !== undefined);
                if (!entries.length) return '';
                const items = entries.map(([name, value]) => `<li><strong>${labels[name] || name}</strong>: ${value}</li>`);
                return `<div class="mb-2 text-left"><strong>${title}</strong><ul class="pl-3 m-0 d-flex flex-column justify-content-start text-left">${items.join('')}</ul></div>`;
            },
            translateOfferType,
            addOfferToVariant(gtin) {
                const offerId = this.offer?.id;
                this.requestCatalogOffers
                    .id(`add-${gtin}`)
                    .post({ requestParams: { id: offerId, variant: gtin } })
                    .then(() => {
                        this.$toastr.success(this.$t('offerHasBeenSuccessfullyAddedToVariant'));
                        this.$router.push(this.parentRoute);
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            ignoreProposal() {
                const offerId = this.offer?.id;
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmIgnoringSuggestion'), {
                        title: this.$t('ignoreSuggestion'),
                        okTitle: this.$t('ignore'),
                        cancelTitle: this.$t('cancel'),
                        centered: true,
                    })
                    .then(value => {
                        if (value) {
                            this.requestOfferModeration
                                .id('delete')
                                .delete({ urlParams: { offerId } })
                                .then(() => {
                                    this.$toastr.success(this.$t('proposalHasBeenSuccessfullyIgnored'));
                                    this.$router.push(this.parentRoute);
                                })
                                .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
                        }
                    });
            },
            fetchProductsData() {
                this.products.forEach(productEntry => {
                    if (productEntry.productId) {
                        this.fetchProduct(productEntry.productId);
                        this.fetchProductVariants(productEntry.productId);
                    }
                });
            },
            fetchProduct(productId) {
                return this.requestCatalogProducts
                    .id(`get-${productId}`)
                    .get({ urlParams: { id: productId } })
                    .catch(() => null);
            },
            fetchProductVariants(productId) {
                return this.requestProductVariants
                    .id(`product-${productId}`)
                    .get({ requestParams: { product: productId } })
                    .catch(() => null);
            },
            fetchVariantOffers(variantGtin) {
                return this.requestVariantOffers
                    .id(variantGtin)
                    .get({ urlParams: { id: variantGtin } })
                    .catch(() => null);
            },
            createItemUrl(item) {
                return {
                    name: 'OffersDetails',
                    params: { id: item.id, parentUrl: this.$route.fullPath },
                    query: {},
                };
            },
        },
        created() {
            this.fetchOffer();
        },
    };
</script>



<style lang="scss" scoped>
    .grid-template {
        display: grid;
        grid-template-columns: minmax(90px, max-content) auto;
        column-gap: 1rem;
        row-gap: 0.4rem;
        align-items: center;

        .label {
            font-weight: bold;
        }
    }
</style>
